import { CheckCircle, Heart, Lightbulb, Shield } from 'lucide-react';
import CTAButton from '../components/CTAButton';

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About Quick Close Home Offers</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            We help homeowners sell their houses quickly, easily, and with confidence. No agents. No listings. No complicated process. Just a straightforward cash offer and a timeline that works for you.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Who We Are</h2>
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 mb-12">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Quick Close Home Offers is a direct home-buying company that helps homeowners sell their properties without the delays, repairs, or uncertainty of the traditional real estate process. We work directly with homeowners to provide a faster, more straightforward selling option.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              For over five years, our team has been actively involved in residential real estate, working hands-on with properties and homeowners across New Jersey, West Virginia, Ohio, Indiana, Texas, Kentucky, Michigan, Pennsylvania, and Florida. This experience allows us to understand different market conditions, property types, and seller situations, so we can offer clear guidance and realistic solutions.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We are not real estate agents, and we don't list homes for sale. Instead, we provide fair, no-obligation cash offers, purchase properties as-is, and manage the process from start to finish on a timeline that works for the seller — without the need for repairs, showings, or added expenses.
            </p>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Mission</h2>
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 mb-12">
            <p className="text-lg text-gray-600 leading-relaxed">
              To make selling your home simple, transparent, and stress-free. We believe homeowners deserve a straightforward option that respects their time, their privacy, and their circumstances.
            </p>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 mb-8">What We Value</h2>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <Heart className="w-8 h-8 text-blue-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Honesty & Integrity</h3>
              <p className="text-gray-600">
                We tell you what we'll offer and why. No hidden agendas, no bait-and-switch, no pressure tactics.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <Lightbulb className="w-8 h-8 text-blue-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Clarity & Transparency</h3>
              <p className="text-gray-600">
                You should understand every step of the process and know exactly what to expect at closing.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <Shield className="w-8 h-8 text-blue-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Seller-First Approach</h3>
              <p className="text-gray-600">
                Your timeline, your situation, your needs come first. We work around your schedule, not ours.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <CheckCircle className="w-8 h-8 text-blue-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Professionalism</h3>
              <p className="text-gray-600">
                We handle everything with care and respect. Your home is important to us.
              </p>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 mb-8">What We're Not</h2>
          <div className="bg-blue-50 border-l-4 border-blue-900 p-8 rounded-lg mb-12">
            <ul className="space-y-4">
              <li className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-blue-900 flex-shrink-0 mt-1" />
                <div>
                  <strong className="text-gray-900">Not a Real Estate Agent or Broker</strong>
                  <p className="text-gray-600 text-sm mt-1">We don't list your home or charge commissions.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-blue-900 flex-shrink-0 mt-1" />
                <div>
                  <strong className="text-gray-900">Not Charging Hidden Fees</strong>
                  <p className="text-gray-600 text-sm mt-1">What we offer is what you get. No surprise deductions or last-minute fees.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-blue-900 flex-shrink-0 mt-1" />
                <div>
                  <strong className="text-gray-900">Not a Loan Company</strong>
                  <p className="text-gray-600 text-sm mt-1">We offer cash. There's no appraisal, no inspection contingency, no financing drama.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-blue-900 flex-shrink-0 mt-1" />
                <div>
                  <strong className="text-gray-900">Not Predatory</strong>
                  <p className="text-gray-600 text-sm mt-1">We make fair offers. If something doesn't feel right, you can walk away anytime—no obligation.</p>
                </div>
              </li>
            </ul>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 mb-8">For Homeowners Who Want a Better Option</h2>
          <div className="bg-blue-50 border border-blue-900 p-8 rounded-xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              If you're tired of the traditional home-selling process—dealing with agents, sitting through open houses, waiting months for a sale, and dealing with buyer contingencies—we're here as an alternative.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              You're in control. You choose when to close. You decide whether to accept an offer. We're just here to give you options and make the process as smooth as possible.
            </p>
          </div>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to learn more?</h3>
            <p className="text-gray-600 mb-8">Getting a cash offer takes just a few minutes and there's no obligation.</p>
            <CTAButton text="Request Your Offer" size="lg" />
          </div>
        </div>
      </section>
    </div>
  );
}
